import type {CancellablePromise} from '@yandex-data-ui/sdk';

import type {
    PageAdditionalFieldsResponse,
    PageBaseFieldsResponse,
    PageDetailsField,
} from '~shared/models';
import type {ArrayValue, Compute} from '~shared/types';
import type {
    GetPageDetailsArgs,
    GetPageDetailsByIdArgs,
} from '~shared/schema/wiki/actions/get-page-details';

import type {CreatePageArgs, ImportPageArgs} from '~shared/schema/wiki/actions/create-page';
import type {UpdatePageArgs} from '~shared/schema/wiki/actions/update-page-details';

import {sdk} from './sdk';

export type GetPageResponse<Fields extends readonly PageDetailsField[]> = Fields extends unknown
    ? Compute<PageBaseFieldsResponse & Pick<PageAdditionalFieldsResponse, ArrayValue<Fields>>>
    : never;

// ===================================================================
// IMPORTANT NOTE!
// in the functions below, we use and overloads
// so that TS doesn't complain that return type is not
// assignable to a dynamic type that we build using `GetPageResponse`.
// However, we can safely assume that it's correct, since backend
// guarantees such response type.
// ===================================================================

export function getPageDetails<Fields extends readonly PageDetailsField[] = []>(
    args: GetPageDetailsArgs<Fields>,
): CancellablePromise<GetPageResponse<Fields>>;
export function getPageDetails(args: GetPageDetailsArgs) {
    return sdk.wiki.getPageDetails(args);
}

export function getPageDetailsById<Fields extends readonly PageDetailsField[] = []>(
    args: GetPageDetailsByIdArgs<Fields>,
): CancellablePromise<GetPageResponse<Fields>>;
export function getPageDetailsById(args: GetPageDetailsByIdArgs) {
    return sdk.wiki.getPageDetailsById(args);
}

export function updatePageDetails<Fields extends readonly PageDetailsField[] = []>(
    args: UpdatePageArgs<Fields>,
): CancellablePromise<GetPageResponse<Fields>>;
export function updatePageDetails(args: UpdatePageArgs) {
    return sdk.wiki.updatePageDetails(args);
}

export function createPage<Fields extends readonly PageDetailsField[] = []>(
    args: CreatePageArgs<Fields>,
): CancellablePromise<GetPageResponse<Fields>>;
export function createPage(args: CreatePageArgs) {
    return sdk.wiki.createPage(args);
}

export function importPage<Fields extends readonly PageDetailsField[] = []>(
    args: ImportPageArgs<Fields>,
): CancellablePromise<GetPageResponse<Fields>>;
export function importPage(args: ImportPageArgs) {
    return sdk.wiki.importPage(args);
}
